import moment from 'moment'
import { find } from 'lodash'
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react'
import SwipeableViews from 'react-swipeable-views'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  cardPaymentMethodsSelector,
  cardUpcomingPaymentsSelector,
  createAutoPaymentRequestSelector,
  createPaymentMethodRequestSelector,
  createPaymentRequestSelector,
  creditCardIsLoadingSelector,
  currentCCLedgerSelector,
  deleteAutoPaymentRequestSelector,
} from 'components/features/creditCard/creditCardStore/selectors'
import {
  CreateCCAutoPaymentAction,
  CreateCCPaymentAction,
  ResetCreateCCAutoPaymentAction,
  ResetCreateCCPaymentAction,
  ResetCreateCCPaymentMethodAction,
  ResetDeleteCCAutoPaymentAction,
} from 'components/features/creditCard/creditCardStore/actions'
import { createCardAutoPaymentSchema, createCardPaymentSchema } from 'components/features/creditCard/creditCardStore/schemas'
import { CCAutoPaymentFormType } from 'components/features/creditCard/creditCardStore/types'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import PayCardAmountStep from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardAmountStep'
import PayCardConfirmStep from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardConfirmStep'
import PayCardDateStep from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardDateStep'
import PayCardMethodSelectStep from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardMethodSelectStep'
import PayCardMethodAddStep from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardMethodAddStep'
import { CreditCardFloatingPane, CREDIT_CARD_PANE, PayCardPaneStep } from 'components/features/creditCard/creditCardStore/floatingPane'
import { PayCardSuccessContent } from 'components/features/creditCard/creditCardPanes/PayCardPane/PayCardSuccessPane'
import { BaseFloatingPane } from 'interfaces/floatingPanes'
import { ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { BACK_BUTTON_PROPS, PayCardAmountPaneButton, PayCardDatePaneButton, PayCardMethodPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { useFieldErrors, useRequestErrors } from 'hooks/useErrors'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import { usePaymentMethodForm } from 'components/features/creditCard/creditCardPanes/paneForms/AddPaymentMethodForm'
import { sleep } from 'helpers/utilsHelpers'
import AutoPayCardDateStep from 'components/features/creditCard/creditCardPanes/PayCardPane/AutoPayCardDateStep'

type PayCardPaneProps =
  & CreditCardFloatingPane
  & {
    step: PayCardPaneStep
  }

type PayCardPaneContent =
  & BaseFloatingPane
  & {
    content: ReactNode
    stepNumber: number
    backButton?: ReactNode
  }

/**
 * This Component hosts the entire card payment process.
 * The first four steps of the form are swipeable.
*/
const PayCardPane: FC<PayCardPaneProps> = ({ step: currentStep, mode, backButton }) => {
  const isAutoPay = mode === 'autoPay'
  const isLoading = useStoreSelector(creditCardIsLoadingSelector)
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const autoPaymentRequest = useStoreSelector(createAutoPaymentRequestSelector)
  const paymentRequest = useStoreSelector(createPaymentRequestSelector)
  const turnOffAutoPaymentRequest = useStoreSelector(deleteAutoPaymentRequestSelector)
  const requestWithErrors = useMemo(() => {
    if (isAutoPay) {
      return autoPaymentRequest.errorsMap ? autoPaymentRequest : turnOffAutoPaymentRequest
    } else {
      return paymentRequest
    }
  }, [autoPaymentRequest, isAutoPay, paymentRequest, turnOffAutoPaymentRequest])
  const paymentMethodRequest = useStoreSelector(createPaymentMethodRequestSelector)
  const paymentMethods = useStoreSelector(cardPaymentMethodsSelector)
  const paymentMethod = !!paymentMethods && paymentMethods.length > 0 && paymentMethods[0]
  const paymentMethodForm = usePaymentMethodForm()

  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const autoPayment = !!upcomingPayments ? find(upcomingPayments, ['schedule_type', 'autopay']) : undefined

  const formContext = useForm<CCAutoPaymentFormType>({
    reValidateMode: 'onSubmit',
    resolver: yupResolver(isAutoPay ? createCardAutoPaymentSchema : createCardPaymentSchema),
    defaultValues: {
      payment_method: paymentMethod ? paymentMethod.uid : '',
      payment_date: !isAutoPay ? moment().format('YYYY-MM-DD') : undefined,
      payment_amount_type: isAutoPay && autoPayment ? autoPayment.payment_amount_type : 'minimum',
      amount: '0',
      fixed_amount: ledger?.minimum_payment_remaining,
      days_before_due_date: isAutoPay ? 5 : undefined, // For AutoPays
    },
  })

  const PAYMENT_STEPS: Record<PayCardPaneStep, PayCardPaneContent> = useMemo(() => ({
    amount: {
      stepNumber: 1,
      content: <PayCardAmountStep key='amount' />,
    },
    date: {
      stepNumber: 2,
      content: !isAutoPay ? <PayCardDateStep key='date' /> : <AutoPayCardDateStep key='date' />,
      backButton: <PayCardAmountPaneButton { ...BACK_BUTTON_PROPS } />,
    },
    paymentMethod: {
      stepNumber: 3,
      content: !!paymentMethod ? <PayCardMethodSelectStep key='method' /> : <PayCardMethodAddStep formContext={ paymentMethodForm } key='method' />,
      backButton: <PayCardDatePaneButton { ...BACK_BUTTON_PROPS } />,
    },
    confirm: {
      stepNumber: 4,
      content: <PayCardConfirmStep key='confirm' />,
      backButton: <PayCardMethodPaneButton { ...BACK_BUTTON_PROPS } />,
    },
    success: {
      stepNumber: 5,
      content: <PayCardSuccessContent key='success' />,
    },
  }), [isAutoPay, paymentMethod, paymentMethodForm])

  const getIsAddPaymentMethodStep = useCallback((slug: PayCardPaneStep) => slug === 'paymentMethod' && !paymentMethod, [paymentMethod])
  const isAddPaymentMethodStep = getIsAddPaymentMethodStep(currentStep)
  const fieldErrors = useFieldErrors(formContext)
  const addPaymentMethodRequestErrors = useRequestErrors({ request: paymentMethodRequest })
  const requestErrors = useRequestErrors({ request: requestWithErrors, setError: formContext.setError })
  const paymentErrors = useMemo(() => fieldErrors ? [fieldErrors] : requestErrors, [fieldErrors, requestErrors])
  const errors = useMemo(() => (
    isAddPaymentMethodStep ? addPaymentMethodRequestErrors : paymentErrors
  ), [addPaymentMethodRequestErrors, isAddPaymentMethodStep, paymentErrors])

  const dispatch = useStoreDispatch()
  const handleDismissErrors = useCallback(() => {
    if (isAddPaymentMethodStep) {
      dispatch(new ResetCreateCCPaymentMethodAction())
      paymentMethodForm.clearErrors()
    } else {
      dispatch(new ResetCreateCCPaymentAction())
      dispatch(new ResetCreateCCAutoPaymentAction())
      dispatch(new ResetDeleteCCAutoPaymentAction())
      formContext.clearErrors()
    }
  }, [dispatch, formContext, isAddPaymentMethodStep, paymentMethodForm])

  const handleSuccessSubmit = useMemo(() => formContext.handleSubmit(async (values) => {
    await sleep(500)
    handleDismissErrors()
    const actionToDispatch = mode === 'autoPay' ? (
      new CreateCCAutoPaymentAction(values)
    ) : (
      new CreateCCPaymentAction(values)
    )
    dispatch(actionToDispatch)
  }), [dispatch, formContext, handleDismissErrors, mode])

  const ErrorNotification = useMemo(() => !!errors && errors.length >= 1 && (
    <FloatingPaneNotification
      messages={ errors }
      onDismiss={ handleDismissErrors }
    />
  ), [errors, handleDismissErrors])

  // -------------------------- Swipe Functionality --------------------------
  const isStepSwipeable = (slug: PayCardPaneStep) => !(slug === 'confirm' && !paymentMethod) && slug !== 'success'
  const swipeableSteps = Object.keys(PAYMENT_STEPS).filter(isStepSwipeable)
  const contentChildren = swipeableSteps.map((stepSlug: PayCardPaneStep, idx: number) => !getIsAddPaymentMethodStep(stepSlug) ? (
    <FormPreset key={ idx } formContext={ formContext } onSubmit={ handleSuccessSubmit }>
      { PAYMENT_STEPS[stepSlug].content }
    </FormPreset>
  ) : (
    // Don't wrap the content in a formContext since PaymentMethodForm has its own context
    PAYMENT_STEPS[stepSlug].content
  ))

  const currentStepNumber = useMemo(() => PAYMENT_STEPS[currentStep].stepNumber, [PAYMENT_STEPS, currentStep])
  const getStepSlug = useCallback((idx: number) => swipeableSteps.find(
    (stepSlug: PayCardPaneStep) => PAYMENT_STEPS[stepSlug].stepNumber === idx + 1 ? stepSlug : undefined
  ), [PAYMENT_STEPS, swipeableSteps])

  const handleSwipe = useCallback((nextSlideIndex) => {
    const nextStep = getStepSlug(nextSlideIndex) as PayCardPaneStep
    dispatch(new ModifyFloatingPaneAction({
      type: CREDIT_CARD_PANE,
      mode: 'payCard',
      step: getStepSlug(nextSlideIndex) as PayCardPaneStep,
      backButton: backButton || PAYMENT_STEPS[nextStep].backButton,
    }))
  }, [PAYMENT_STEPS, backButton, dispatch, getStepSlug])

  useEffect(() => {
    handleDismissErrors()
  }, [currentStepNumber, handleDismissErrors])

  const progressPercent = useMemo(() => {
    if (isLoading) {
      return undefined
    } else if (isAddPaymentMethodStep && !paymentMethodRequest?.isLoading) {
      return (currentStepNumber - .5) * 20
    }
    return currentStepNumber * 20
  }, [currentStepNumber, isAddPaymentMethodStep, paymentMethodRequest, isLoading])

  const swipeableViews = (
    <SwipeableViews
      onChangeIndex={ handleSwipe }
      index={ currentStepNumber - 1 }
      children={ contentChildren }
      animateHeight
    />
  )
  // ---------------------- End - Swipe functionality -------------------------

  return (
    <CreditCardPanePreset
      title={ isAutoPay ? 'Manage Auto-Pay' : 'Make a Payment' }
      isLoading={ isLoading }
      notification={ ErrorNotification }
      progressBarPercent={ progressPercent }
      tertiaryButton={ currentStep === 'amount' ? undefined : null }
      noPadding
    >
      { currentStep === 'success' ? <PayCardSuccessContent /> : swipeableViews }
    </CreditCardPanePreset>
  )
}

export default PayCardPane
